import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Download, Mail, FileSpreadsheet, FileText } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ExportPage() {
  const [payrolls, setPayrolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPayrolls, setSelectedPayrolls] = useState([]);
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [emailForm, setEmailForm] = useState({
    recipient_email: '',
    subject: 'Lohnabrechnungen / Fiches de paie',
    body: 'Anbei die Lohnabrechnungen für den angegebenen Zeitraum.\n\nCi-joint les fiches de paie pour la période indiquée.'
  });
  const [sending, setSending] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    setPeriodStart(start.toISOString().split('T')[0]);
    setPeriodEnd(end.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    const fetchPayrolls = async () => {
      try {
        const response = await axios.get(`${API}/payroll`, {
          params: { status: 'finalized', period_start: periodStart, period_end: periodEnd }
        });
        setPayrolls(response.data);
      } catch (error) {
        console.error('Error fetching payrolls:', error);
      } finally {
        setLoading(false);
      }
    };
    
    if (periodStart && periodEnd) {
      fetchPayrolls();
    }
  }, [periodStart, periodEnd]);

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedPayrolls(payrolls.map(p => p.id));
    } else {
      setSelectedPayrolls([]);
    }
  };

  const handleSelectPayroll = (id, checked) => {
    if (checked) {
      setSelectedPayrolls(prev => [...prev, id]);
    } else {
      setSelectedPayrolls(prev => prev.filter(p => p !== id));
    }
  };

  const handleDownloadPdf = async (id, employeeName, period) => {
    try {
      const response = await axios.get(`${API}/export/pdf/${id}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Lohnabrechnung_${employeeName.replace(' ', '_')}_${period}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error(t('errorOccurred'));
    }
  };

  const handleDownloadExcel = async () => {
    try {
      const response = await axios.get(`${API}/export/excel`, {
        params: { period_start: periodStart, period_end: periodEnd },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Lohnabrechnung_${periodStart}_${periodEnd}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel downloaded');
    } catch (error) {
      console.error('Error downloading Excel:', error);
      toast.error(t('errorOccurred'));
    }
  };

  const handleSendEmail = async () => {
    if (!emailForm.recipient_email || selectedPayrolls.length === 0) {
      toast.error('Please select payrolls and enter recipient email');
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API}/send-email`, {
        recipient_email: emailForm.recipient_email,
        subject: emailForm.subject,
        body: emailForm.body,
        payroll_ids: selectedPayrolls
      });
      toast.success(t('emailSent'));
    } catch (error) {
      console.error('Error sending email:', error);
      const detail = error.response?.data?.detail;
      let errorMessage = t('smtpNotConfigured');
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        errorMessage = detail.map(e => e.msg || JSON.stringify(e)).join(', ');
      }
      toast.error(errorMessage);
    } finally {
      setSending(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3
    }).format(value || 0) + ' TND';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#E31E24] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="export-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-3xl font-bold text-[#1A1A1A]">{t('export')}</h1>
        <Button
          onClick={handleDownloadExcel}
          disabled={payrolls.length === 0}
          className="bg-emerald-600 hover:bg-emerald-700 text-[#1A1A1A]"
          data-testid="download-excel-btn"
        >
          <FileSpreadsheet className="w-4 h-4 mr-2" />
          {t('exportExcel')}
        </Button>
      </div>

      {/* Period Filter */}
      <Card className="bg-white border-gray-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="space-y-2">
              <Label className="text-gray-500">{t('periodStart')}</Label>
              <Input
                type="date"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
                className="bg-white border-gray-200 text-[#1A1A1A]"
                data-testid="period-start-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-500">{t('periodEnd')}</Label>
              <Input
                type="date"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
                className="bg-white border-gray-200 text-[#1A1A1A]"
                data-testid="period-end-input"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Payroll List */}
        <div className="lg:col-span-2">
          <Card className="bg-white border-gray-200">
            <CardHeader>
              <CardTitle className="text-[#1A1A1A]">Finalisierte Abrechnungen</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-gray-200 hover:bg-transparent">
                      <TableHead className="table-header w-12">
                        <Checkbox
                          checked={selectedPayrolls.length === payrolls.length && payrolls.length > 0}
                          onCheckedChange={handleSelectAll}
                          data-testid="select-all-checkbox"
                        />
                      </TableHead>
                      <TableHead className="table-header">{t('employees')}</TableHead>
                      <TableHead className="table-header">{t('payrollPeriod')}</TableHead>
                      <TableHead className="table-header">{t('totalGross')}</TableHead>
                      <TableHead className="table-header text-right">{t('actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payrolls.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-gray-400">
                          {t('noData')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      payrolls.map((p) => (
                        <TableRow key={p.id} className="table-row">
                          <TableCell>
                            <Checkbox
                              checked={selectedPayrolls.includes(p.id)}
                              onCheckedChange={(checked) => handleSelectPayroll(p.id, checked)}
                              data-testid={`select-payroll-${p.id}`}
                            />
                          </TableCell>
                          <TableCell className="text-[#1A1A1A]">{p.employee_name}</TableCell>
                          <TableCell className="text-gray-500 font-mono text-sm">
                            {p.period_start}
                          </TableCell>
                          <TableCell className="font-mono text-[#1A1A1A]">
                            {formatCurrency(p.total_gross)}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownloadPdf(p.id, p.employee_name, p.period_start)}
                              className="text-[#E31E24] hover:text-[#FF4D52]"
                              data-testid={`download-pdf-${p.id}`}
                            >
                              <FileText className="w-4 h-4 mr-1" />
                              PDF
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Email Form */}
        <div>
          <Card className="bg-white border-gray-200">
            <CardHeader>
              <CardTitle className="text-[#1A1A1A] flex items-center gap-2">
                <Mail className="w-5 h-5 text-[#E31E24]" />
                {t('sendEmail')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-gray-500">{t('recipientEmail')}</Label>
                <Input
                  type="email"
                  value={emailForm.recipient_email}
                  onChange={(e) => setEmailForm(prev => ({ ...prev, recipient_email: e.target.value }))}
                  className="bg-white border-gray-200 text-[#1A1A1A]"
                  placeholder="steuerberater@example.com"
                  data-testid="recipient-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-500">{t('subject')}</Label>
                <Input
                  value={emailForm.subject}
                  onChange={(e) => setEmailForm(prev => ({ ...prev, subject: e.target.value }))}
                  className="bg-white border-gray-200 text-[#1A1A1A]"
                  data-testid="subject-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-500">{t('message')}</Label>
                <Textarea
                  value={emailForm.body}
                  onChange={(e) => setEmailForm(prev => ({ ...prev, body: e.target.value }))}
                  className="bg-white border-gray-200 text-[#1A1A1A] min-h-[120px]"
                  data-testid="message-textarea"
                />
              </div>
              <div className="pt-2">
                <p className="text-xs text-gray-400 mb-3">
                  {selectedPayrolls.length} {t('attachPayrolls')}
                </p>
                <Button
                  onClick={handleSendEmail}
                  disabled={sending || selectedPayrolls.length === 0 || !emailForm.recipient_email}
                  className="w-full bg-[#E31E24] hover:bg-[#C41218] text-[#1A1A1A] btn-primary-glow"
                  data-testid="send-email-btn"
                >
                  {sending ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {t('loading')}
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      {t('sendEmail')}
                    </span>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
